import Vue, { CreateElement } from 'vue'
import { Button } from '@src/elements/button/button'
import WorkoutData, { HolderType, WorkoutType } from '@src/data/workout-data'
import { WorkoutService } from '@src/service/workout'
import { WorkoutExercise } from '@src/data/workout-exercise'
import Club from '@src/data/club'
import Member from '@src/data/member'
import { Component } from 'vue-property-decorator'
import { MemberProfile } from '@src/data/member-profile'
import { StationDependentExercises } from '@src/routes/content/components/workout-builder/station-dependent-exercises'
import { defaultWorkoutFilters } from '@src/state/dafaults'

@Component({
  computed: {
    selectedExercises () {
      return this.$store.state.new_workout.workoutExercises
    },
    workout () {
      return this.$store.state.new_workout
    },
    club () {
      return this.$store.state.club
    },
    member () {
      return this.$store.state.workoutMember || this.$store.state.memberProfile
    }
  }
})
export class SaveWorkout extends Vue {

  protected showConfirmPopup: boolean = false

  protected workoutService: WorkoutService = new WorkoutService()

  protected loading: boolean = true
  protected selectedExercises: WorkoutExercise[]
  protected workout: WorkoutData
  protected club: Club

  // member to which the workout will be assigned to
  protected member: Member | MemberProfile | null

  // workout is member or club owned (value to be used to determine the type of workout to be created);
  // if the workout is club owned, and a member is selected, another member owned workout needs to be created
  protected holderType: HolderType = HolderType.CLUB

  private showStationDependentSettings: boolean = false

  private saveInProgress: boolean = false

  render (h: CreateElement) {
    return <div>
      <Button type='elevated' class='save-btn' label={this.$t('SAVE')} click={this.previewSave} icon={this.saveInProgress ? '../img/loader' : ''}
              disabled={(this.selectedExercises && this.selectedExercises.length === 0) || (this.workout && !this.workout.name.trim()) || this.saveInProgress} slot='topRight' />

      {this.showStationDependentSettings ? <StationDependentExercises showModal={this.showStationDependentSettings}
                                                                      closeCallback={() => {
                                                                        this.showStationDependentSettings = false
                                                                      }}
                                                                      saveCallback={this.saveWorkout} /> : null}
    </div>
  }

  previewSave () {
    if (this.workout.type === WorkoutType.HOME || !this.workout.stationLocked) {
      // save and exit if this is a home workout
      return this.saveWorkout()
    } else {
      // continue to workout save options
      this.showStationDependentSettings = true
    }
  }

  /**
   * Save the workout (create or edit) and assign to a member if needed.
   *
   * Steps:
   * - build the workout (based on different settings, formatted exercises, station - exercise setup)
   * - create or edit the workout
   * - if a new member is selected, create a new member owned workout; activate the new workout for the member
   * - execute the after save actions (exit builder or continue viewing the builder)
   */
  async saveWorkout () {
    this.holderType = this.member ? HolderType.MEMBER : HolderType.CLUB
    if (!this.saveInProgress) {
      this.saveInProgress = true

      const newWorkout = this.buildNewWorkout()

      // submit data (add or edit)
      if (this.$store.state.new_workout.id) {
        await this.editWorkout(this.$store.state.new_workout.id, newWorkout)
      } else {
        await this.createWorkout(newWorkout)
        this.$store.dispatch('filterWorkouts', defaultWorkoutFilters())
      }

      this.saveInProgress = false
    }
  }

  buildNewWorkout () {
    if (this.showStationDependentSettings) {
      let newWorkoutExercises: WorkoutExercise[] = []
      this.$store.state.stations.forEach((station) => {
        station.exercises.forEach((exercise) => {
          newWorkoutExercises.push(exercise)
        })
      })
      this.workout.workoutExercises = newWorkoutExercises
    }
    return this.workoutService.buildWorkout(this.workout, this.selectedExercises, this.$store.state.club.screenExerciseLen, this.$store.state.stations,
      this.holderType, this.$store.state.club.id, this.member ? this.member.id : null)
  }

  async createWorkout (workout) {
    let newWorkoutId = await this.workoutService.add(workout)
    if (this.member) {
      await this.activateWorkoutToMember(newWorkoutId)
      return
    }
    this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.SAVE_NOTIFICATION'), fadeOut: true })
    this.closeBuilder()
  }

  async editWorkout (workoutId: number, workout) {
    if (this.member) {
      if (this.workout.holderType === HolderType.MEMBER) {
        // edit individual workout
        await this.workoutService.edit(this.workout.id, workout)
      } else {
        // create member owned copy and assign it to member
        let workoutToBeAssigned = await this.createMemberOwnedWorkoutCopy(workout)
        await this.activateWorkoutToMember(workoutToBeAssigned)
        return
      }
    } else {
      // edit club wo only if no member was assigned
      await this.workoutService.edit(workoutId, workout)
    }
    this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.SAVE_NOTIFICATION'), fadeOut: true })
    this.closeBuilder()
  }

  async createMemberOwnedWorkoutCopy (memberWorkout) {
    if (this.member) {
      memberWorkout.holder_id = this.member.id
      memberWorkout.holder_type = HolderType.MEMBER

      const newWorkoutId = await this.workoutService.add(memberWorkout, this.workout.id)
      return newWorkoutId
    }
    return null
  }

  async activateWorkoutToMember (workoutId) {
    if (this.member) {

      await this.workoutService.activateWorkout(workoutId, this.member.id)

      this.$store.dispatch('setCheckupWorkout', workoutId)
      this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUT.ASSIGN_NOTIFICATION', { name: this.member!.fullName }), fadeOut: true })

      this.closeBuilder()
    }
  }

  closeBuilder () {
    this.$router.go(-1)
  }
}
